import api from '@pray/shared/utils/api';

/**
 * Fetch giving payments data (subscribers, transactions, or organizations)
 *
 * @param {Object} options
 * @param {string} options.artistId - Artist ID (required)
 * @param {string} options.startDate - Start date ISO string (required)
 * @param {string} options.endDate - End date ISO string (required)
 * @param {string} [options.offset] - Pagination offset
 * @param {string} [options.limit] - Pagination limit
 * @param {boolean} [options.downloadCsv] - Download as CSV
 * @param {string} [options.viewType] - View type: 'subscribers' | 'transactions' | 'organizations'
 * @param {string} [options.intervalFilter] - Filter by interval: 'all' | 'week' | 'month' | 'year'
 * @param {string} [options.statusFilter] - Filter by status: 'all' | 'active' | 'cancelled' | 'trial'
 * @param {string} [options.search] - Search by name or email
 * @param {string} [options.sortBy] - Sort field: 'start_date' | 'first_name' | 'last_name' | 'amount' | 'total_revenue' | 'renewals'
 * @param {string} [options.sortOrder] - Sort order: 'asc' | 'desc'
 */
export async function getGivingPayments({
  artistId = '',
  startDate = '',
  endDate = '',
  offset = '',
  limit = '',
  downloadCsv = false,
  viewType = 'subscribers',
  // New filter/sort params
  intervalFilter = 'all',
  statusFilter = 'all',
  search = '',
  sortBy = 'start_date',
  sortOrder = 'desc',
}) {
  if (!artistId) throw new Error('Artist ID is required');
  if (!startDate) throw new Error('Start Date is required');
  if (!endDate) throw new Error('End Date is required');

  const params = {
    start_date: startDate,
    end_date: endDate,
    view_type: viewType,
  };

  if (offset) params.offset = offset;
  if (limit) params.limit = limit;

  // Add filter params (for subscribers view)
  if (viewType === 'subscribers') {
    if (intervalFilter && intervalFilter !== 'all') {
      params.interval_filter = intervalFilter;
    }
    if (statusFilter && statusFilter !== 'all') {
      params.status_filter = statusFilter;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (sortBy) {
      params.sort_by = sortBy;
    }
    if (sortOrder) {
      params.sort_order = sortOrder;
    }
  }

  const options = {};

  if (downloadCsv) {
    params.download_csv = true;
    options.responseType = 'blob';
  }

  const url = `/web/studio/artists/${artistId}/giving-payments`;
  const response = await api.executeGetRequest(url, params, options);

  return response.data;
}
